/**
 * The four non-populated states, in one place.
 *
 * Every Projects surface ships loading / empty / error / permission-denied, and
 * they are here rather than re-written per tab so they cannot drift apart —
 * inconsistent empty states are the single most common way a feature reads as
 * unfinished.
 *
 * Loading is a SKELETON, not a spinner: the layout that is about to appear is
 * already visible, so nothing jumps when the data lands.
 */

import React from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import './StateBlocks.css';

export function SkeletonRows({ rows = 3, tall = false }: { rows?: number; tall?: boolean }) {
  const { t } = useLanguage();
  return (
    <div
      className={`pj-skeleton ${tall ? 'pj-skeleton-tall' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={t('common.loading') || 'Loading'}
    >
      {Array.from({ length: rows }).map((_, index) => (
        <div className="pj-skeleton-row" key={index}>
          <div className="pj-skeleton-avatar" />
          <div className="pj-skeleton-lines">
            <div className="pj-skeleton-line pj-skeleton-line-strong" />
            <div className="pj-skeleton-line" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface EmptyBlockProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  /** The one thing to do next. Omitted when the viewer can't act. */
  action?: React.ReactNode;
}

export function EmptyBlock({ icon, title, body, action }: EmptyBlockProps) {
  return (
    <div className="pj-state pj-state-empty">
      <div className="pj-state-icon" aria-hidden="true">{icon}</div>
      <h3 className="pj-state-title">{title}</h3>
      <p className="pj-state-body">{body}</p>
      {action && <div className="pj-state-action">{action}</div>}
    </div>
  );
}

export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useLanguage();
  return (
    <div className="pj-state pj-state-error" role="alert">
      <div className="pj-state-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="13" />
          <circle cx="12" cy="16.5" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      </div>
      <h3 className="pj-state-title">{t('projects.errorTitle') || 'Something went wrong'}</h3>
      <p className="pj-state-body">{message}</p>
      {onRetry && (
        <div className="pj-state-action">
          <button type="button" className="pj-btn pj-btn-secondary" onClick={onRetry}>
            {t('common.retry') || 'Try again'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Shown when the project exists but is not this user's to open — or, more often,
 * when it does not exist at all. The backend deliberately answers 404 in both
 * cases so a non-member cannot probe which project ids are real, and this copy
 * is written to be truthful under either reading.
 */
export function DeniedBlock({ onBack }: { onBack?: () => void }) {
  const { t } = useLanguage();
  return (
    <div className="pj-state pj-state-denied" role="alert">
      <div className="pj-state-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="10" width="16" height="10" rx="2" />
          <path d="M8 10V7a4 4 0 0 1 8 0v3" />
        </svg>
      </div>
      <h3 className="pj-state-title">{t('projects.deniedTitle') || 'This project is not available'}</h3>
      <p className="pj-state-body">
        {t('projects.deniedBody')
          || "It may have been closed, or you may no longer have access. Ask a project manager to invite you again."}
      </p>
      {onBack && (
        <div className="pj-state-action">
          <button type="button" className="pj-btn pj-btn-secondary" onClick={onBack}>
            {t('projects.backToProjects') || 'Back to projects'}
          </button>
        </div>
      )}
    </div>
  );
}
